import {
  Area, Bar, CartesianGrid,
  CartesianGridProps, Cell, ComposedChart,
  Legend, Line, Pie, PieChart, PolarAngleAxis, PolarGrid, PolarGridProps,
  PolarRadiusAxis,
  Radar, RadarChart, ResponsiveContainer,
  Scatter, Tooltip,
  XAxis, YAxis
} from "recharts";
import { LayoutType } from "recharts/types/util/types";

type color = string;
export type graficType = "bar" | "line" | "area" | "scatter" | "pie" | "radar";

export interface ChartDataProps {
  // Comum em ambos
  dataKey: string;
  stackId?: string;
  fill?: color;

  typeGraph: graficType;

  // Apenas em gráficos não circulares
  stroke?: color;
  strokeWidth?: number;
  strokeDasharray?: string;
  connectNulls?: boolean;

  // Dados dos gráficos circulares
  cx?: string | number;
  cy?: string | number;
  outerRadius?: number;
  innerRadius?: number;
  label?: boolean;
}

export interface ChartProps {
  id: string;
  title?: string;
  position?: "center" | "start" | "end";
  height: number;
  width?: number;
  layout?: LayoutType;
  grid?: CartesianGridProps | PolarGridProps;
  data: { [key: string]: number | string; }[];
  dataItems?: ChartDataProps[];
}

export const GenericChart: React.FC<ChartProps> = (data) => {
  const otherData = {
    title: data.title,
    position: data.position,
    layout: data.layout,
    grid: data.grid,
    data: data.data,
    dataItems: data.dataItems,
  };

  otherData.data = otherData.data.map((value) => {
    const newValue = Number(value.x);
    value.x = isNaN(newValue) ? value.x : newValue;
    return value;
  });

  const anyPie = otherData.dataItems?.some((value) => value.typeGraph === "pie") ?? false;
  const anyRadar = otherData.dataItems?.some((value) => value.typeGraph === "radar") ?? false;
  const containsBar = otherData.dataItems?.some((value) => value.typeGraph === "bar") ?? false;
  const isAllNum = otherData.data.every((value) => typeof value.x === 'number');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataRadar: { [key: string]: any; } = {};
  if (anyRadar) {
    dataRadar["cx"] = "50%";
    dataRadar["cy"] = "50%";
    dataRadar["outerRadius"] = "80%";
  }
  const TypeGraph = (anyPie ? PieChart : (anyRadar ? RadarChart : ComposedChart));

  return <ResponsiveContainer width={"100%"} height={"100%"}>
    <TypeGraph {...otherData} {...dataRadar}>
      <Tooltip />
      <Legend />

      {!anyPie && !anyRadar && <XAxis dataKey="x" type={(isAllNum && !containsBar) ? "number" : "category"} />}
      {!anyPie && !anyRadar && <YAxis />}

      {data.grid && !anyPie && !anyRadar && <CartesianGrid {...data.grid as CartesianGridProps} />}

      {/* Gráficos circulares */}
      {(anyPie || anyRadar) && generateCircularCharts({
        data: otherData.data,
        isRadar: (!anyPie && anyRadar),
        dataItems: otherData.dataItems,
      })}

      {/* Gráfico em barras, linhas, área e etc */}
      {data.dataItems && !(anyPie || anyRadar) && data.dataItems.map((value, i) => {
        const { typeGraph, fill, ...data } = value;

        if (typeGraph === 'bar') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return <Bar key={i} {...(data as any)} fill={fill} />;
        } else if (typeGraph === "area") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return <Area key={i} {...(data as any)} fill={fill} />;
        } else if (typeGraph === "scatter") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return <Scatter key={i} {...(data as any)} fill={fill} />;
        } else { // Gráfico de linhas
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return <Line key={i} {...(data as any)} fill={fill} />;
        }
      })}
    </TypeGraph>
  </ResponsiveContainer >;
};

interface GenerateRadiusProps {
  isRadar: boolean;
  data: { [key: string]: number | string; }[];
  dataItems?: ChartDataProps[];
}
const generateCircularCharts = ({ data, dataItems, isRadar }: GenerateRadiusProps) => {
  if (!isRadar) {
    return data.map((value, index) => {
      const dadosChart: {
        name: string;
        value: string | number;
        style?: ChartDataProps;
      }[] = [];

      for (const key in value) {
        if (key === "x") continue;
        const style = dataItems?.filter((value) => value.dataKey === key)[0];

        dadosChart.push({
          name: key,
          value: value[key],
          style: style,
        });
      }

      const radiusMax = (100 / data.length);
      const paddingMax = data.length > 1 ? (10 / (data.length - 1)) : 0;

      const innerRadius = (index * radiusMax);
      const outerRadius = innerRadius + radiusMax - paddingMax;


      return <Pie
        key={index}
        dataKey="value"
        data={dadosChart}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
      >
        {dadosChart.map((entry, i) => <Cell key={`cell-${i}`} {...entry.style} />)}
      </Pie>;
    });
  }

  const fullMark = data.reduce(
    (max, item) => Math.max(max, Object.keys(item).reduce(
      (max, key) => Math.max(max, key === "x" ? 0 : Number(item[key])),
      -Infinity
    )),
    -Infinity) * 1.05; // Mais 5%

  return <>
    <PolarGrid />
    <PolarAngleAxis dataKey="x" />
    <PolarRadiusAxis domain={[0, Math.ceil(fullMark)]} />
    {dataItems?.map((value, i) => {
      return <Radar
        key={i}
        name={value.dataKey}
        {...value}
      />;
    })}
  </>;
};